import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Field, Formik, FormikErrors, FormikHelpers } from 'formik';
import { Grid, IconButton, Skeleton } from '@mui/material';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	CerebroPageContainer,
	CerebroBreadcrumb,
	CerebroHeading,
	CerebroText,
	CerebroLabel,
	CerebroInput,
	CerebroPrimaryButton,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroYesNo,
	CerebroDrawer,
	CerebroIcon,
	CerebroTextarea,
	NaicsCodeAutoComplete,
	CerebroAutoComplete,
	CerebroMaskedInput,
	CerebroForm,
} from '../shared/components';

import { CerebroApiEndpoints } from '../shared/utils/api';
import { useApi, useAuth } from '../shared/context';
import { FormikUtils, NumberUtils } from '../shared/utils';
import { getApiResponseData } from '../shared/utils/axios';

import {
	OwnershipStructureMap,
	Industry,
	NaicsCode,
	OwnershipStructure,
	State,
	UpdateDealDto,
	Company,
	CountryTypes,
	CountryTypeMap,
} from '../shared/models';

import {
	useDealGeographiesQuery,
	useDealQuery,
	useStatesQuery,
	useIndustriesQuery,
	useNavigateToNextPage,
	QueryKeys,
} from '../shared/hooks';
import { useCompanyQuery } from '../shared/hooks/Queries/useCompanyQuery';
import { getAndUpdateDeal } from './DealIntroduction';

export const CompanyBorrower: React.FC = () => {
	const snackbar = useSnackbar();

	const api = useApi();
	const { currentUser } = useAuth();

	const navigateToNextPage = useNavigateToNextPage();

	const { guid } = useParams<'guid'>();

	const queryClient = useQueryClient();

	const dealQuery = useDealQuery(guid);
	const statesQuery = useStatesQuery();
	const industriesQuery = useIndustriesQuery();
	const otherGeographiesQuery = useDealGeographiesQuery(guid, 'Borrower');
	const companyQuery = useCompanyQuery(guid, { enabled: dealQuery.isSuccess });

	const dealMutation = useMutation(
		async ({ values }: DealMutationVariables) => {
			const dealPatch: UpdateDealDto = {
				naicsCode: values.naicsCode?.code ?? undefined,
				countryType: values.countryType,
				stateCode: values.stateCode,
				postalCode: values.postalCode,
				companyLocatedInRuralArea: values.companyLocatedInRuralArea ?? false,
				companyIndustryGuid: values.companyIndustry?.guid ?? undefined,
				yearsInOperation: NumberUtils.ParseDecimal(values.yearsInOperation, undefined),
				numberEmployees: NumberUtils.ParseDecimal(values.numberEmployees, undefined),
				nonUsBasedEmployees: NumberUtils.ParseDecimal(values.nonUsBasedEmployees, undefined),
				institutionalInvestor: values.institutionalInvestor,
				b2bPercent: NumberUtils.ParseDecimal(values.b2bPercent, undefined),
				b2cPercent: NumberUtils.ParseDecimal(values.b2cPercent, undefined),
				b2gPercent: NumberUtils.ParseDecimal(values.b2gPercent, undefined),
				customerConcentrationsMoreThan10Percent: values.customerConcentrationsMoreThan10Percent,
				customerConcentrationDetails: values.customerConcentrationsMoreThan10Percent
					? values.customerConcentrationDetails
					: '',
				sourcePage: 'Company',
			};

			if (dealPatch.companyIndustryGuid === '-1') delete dealPatch.companyIndustryGuid;

			const updatedDeal = await getAndUpdateDeal(api, guid, dealPatch);
			let company: Company | null = null;

			const { data, error } = await getApiResponseData<Company>(
				api.patch(CerebroApiEndpoints.Company.Root(guid), {
					ownershipStructure: values.ownershipStructure,
					showCreditExplorer: values.showCreditExplorer,
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			company = data;

			return { updatedDeal, company };
		},
		{
			onSuccess: ({ updatedDeal, company }) => {
				queryClient.setQueryData(QueryKeys.Deal.Root(guid), updatedDeal);
				queryClient.setQueryData(QueryKeys.Company.Root(guid), company);
				queryClient.invalidateQueries(QueryKeys.Matching.Get(guid));
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	const dealGeographiesMutation = useMutation(
		async ({ values }: DealMutationVariables) => {
			const { data, error } = await getApiResponseData<State[]>(
				api.post(CerebroApiEndpoints.DealGeographies(guid, 'Borrower'), values.otherGeographies)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: geographies => {
				queryClient.setQueryData(QueryKeys.Geographies(guid, 'Borrower'), geographies);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	const onSubmit = async (values: CompanyBorrowerFormValues, helpers: FormikHelpers<CompanyBorrowerFormValues>) => {
		var results = await Promise.allSettled([
			dealMutation.mutateAsync({ values, helpers }),
			dealGeographiesMutation.mutateAsync({ values, helpers }),
		]);

		helpers.setSubmitting(false);

		if (results.every(result => result.status === 'fulfilled')) {
			snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
			navigateToNextPage();
		} else {
			snackbar.enqueueSnackbar('Something went wrong, some of your changes could not be saved.', {
				variant: 'error',
			});
		}
	};

	const [ruralAreaInfoOpen, setRuralAreaInfoOpen] = React.useState(false);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Company &gt; Borrower</CerebroBreadcrumb>
			<CerebroHeading>Tell us more about the Borrowing Company</CerebroHeading>
			<CerebroText sx={{ marginTop: 3, marginBottom: 6 }}>
				Provide details on the company needing the financing. The more details, the better as it helps the
				data-driven results be as accurate as possible.
			</CerebroText>

			<Formik
				validate={validateForm}
				enableReinitialize
				onSubmit={onSubmit}
				initialValues={{
					naicsCode: dealQuery.data?.naicsCode ?? null,
					countryType: dealQuery.data?.countryType ?? 'UnitedStates',
					stateCode: dealQuery.data?.state?.code ?? '',
					postalCode: dealQuery.data?.postalCode ?? '',
					otherGeographies: otherGeographiesQuery.data?.map(state => state.code) ?? ([] as string[]),
					companyLocatedInRuralArea: dealQuery.data?.companyLocatedInRuralArea ?? null,
					companyIndustry: dealQuery.data?.companyIndustry ?? null,
					yearsInOperation: NumberUtils.FormatDecimal(dealQuery.data?.yearsInOperation),
					numberEmployees: NumberUtils.FormatDecimal(dealQuery.data?.numberEmployees),
					nonUsBasedEmployees: NumberUtils.FormatDecimal(dealQuery.data?.nonUsBasedEmployees),
					institutionalInvestor: dealQuery.data?.institutionalInvestor ?? false,
					b2bPercent: NumberUtils.FormatDecimal(dealQuery.data?.b2bPercent),
					b2cPercent: NumberUtils.FormatDecimal(dealQuery.data?.b2cPercent),
					b2gPercent: NumberUtils.FormatDecimal(dealQuery.data?.b2gPercent),
					customerConcentrationsMoreThan10Percent:
						dealQuery.data?.customerConcentrationsMoreThan10Percent ?? false,
					customerConcentrationDetails: dealQuery.data?.customerConcentrationDetails ?? '',
					ownershipStructure: companyQuery.data?.ownershipStructure ?? 'Unknown',
					showCreditExplorer: true,
				}}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm onSubmit={handleSubmit}>
						{currentUser?.isEmployeeUser === true && (
							<>
								<CerebroLabel htmlFor="naicsCode">
									What is the company NAICS code?{' '}
									<CerebroText variant="hint" component="span">
										(Employee Only)
									</CerebroText>
								</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="naicsCode"
										name="naicsCode"
										component={NaicsCodeAutoComplete}
										placeholder="6 Digit NAICS Code"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('naicsCode', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
								{FormikUtils.IsInvalid('naicsCode', errors, touched) && (
									<CerebroText variant="error">{errors.naicsCode}</CerebroText>
								)}
							</>
						)}

						<CerebroLabel sx={{ marginTop: 3 }}>Borrowing Entity Location</CerebroLabel>

						<Grid container spacing={3}>
							<Grid item xs={4}>
								{dealQuery.isSuccess && statesQuery.isSuccess ? (
									<Field
										as={CerebroDropdown}
										name="countryType"
										disabled={isSubmitting}
										onChange={(e: any) => {
											const changed = e.target.value as CountryTypes;
											setFieldValue('countryType', changed);
											if (changed != 'UnitedStates') {
												setFieldValue('stateCode', '');
												setFieldValue('postalCode', '');
											}
										}}
									>
										{Object.entries(CountryTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
							{values.countryType == 'UnitedStates' && (
								<Grid item xs={4}>
									{dealQuery.isSuccess && statesQuery.isSuccess ? (
										<Field as={CerebroDropdown} name="stateCode" disabled={isSubmitting}>
											{statesQuery.data.map(state => (
												<CerebroDropdownMenuItem key={state.code} value={state.code}>
													{state.name}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									) : (
										<Skeleton height={60} />
									)}
								</Grid>
							)}
							{values.countryType == 'UnitedStates' && (
								<Grid item xs={4}>
									{dealQuery.isSuccess && statesQuery.isSuccess ? (
										<Field as={CerebroInput} name="postalCode" placeholder="Zip Code" />
									) : (
										<Skeleton height={60} />
									)}
								</Grid>
							)}
						</Grid>

						<CerebroLabel htmlFor="otherGeographies" sx={{ marginTop: 3 }}>
							Other Operating Locations
						</CerebroLabel>
						{dealQuery.isSuccess && statesQuery.isSuccess ? (
							<Field
								id="otherGeographies"
								as={CerebroMultiDropdown}
								name="otherGeographies"
								disabled={isSubmitting}
							>
								{statesQuery.data.map(state => (
									<CerebroMultiDropdownMenuItem key={state.code} value={state.code}>
										{state.name}
									</CerebroMultiDropdownMenuItem>
								))}
							</Field>
						) : (
							<Skeleton height={60} />
						)}

						<CerebroLabel id="companyLocatedInRuralArea" sx={{ marginTop: 3 }}>
							Do you believe your company headquarters or its operating locations are located in a rural
							development zone?{' '}
							<IconButton onClick={() => setRuralAreaInfoOpen(true)}>
								<FontAwesomeIcon icon={faInfoCircle} />
							</IconButton>
						</CerebroLabel>
						{dealQuery.isSuccess ? (
							<Field
								id="companyLocatedInRuralArea"
								as={CerebroYesNo}
								name="companyLocatedInRuralArea"
								disabled={isSubmitting}
							/>
						) : (
							<Skeleton height={60} />
						)}

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item xs={12} md={7}>
								<CerebroLabel
									required
									htmlFor="companyIndustry"
									error={FormikUtils.IsInvalid('companyIndustry', errors, touched)}
								>
									What is the company&apos;s industry?
								</CerebroLabel>
								{dealQuery.isSuccess && industriesQuery.isSuccess ? (
									<Field
										id="companyIndustry"
										component={CerebroAutoComplete}
										name="companyIndustry"
										disabled={isSubmitting}
										options={industriesQuery.data}
										isOptionEqualToValue={(option: Industry, value: Industry) =>
											option.guid === value.guid
										}
										getOptionLabel={(industry: Industry) => industry?.name ?? 'Select Industry'}
										error={FormikUtils.IsInvalid('companyIndustry', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
								{FormikUtils.IsInvalid('companyIndustry', errors, touched) && (
									<CerebroText variant="error">{errors.companyIndustry}</CerebroText>
								)}
							</Grid>

							<Grid item xs={12} md={7}>
								<CerebroLabel
									required
									htmlFor="yearsInOperation"
									error={FormikUtils.IsInvalid('yearsInOperation', errors, touched)}
								>
									How many years has the company been operating?
								</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="yearsInOperation"
										as={CerebroMaskedInput}
										name="yearsInOperation"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('yearsInOperation', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
								{FormikUtils.IsInvalid('yearsInOperation', errors, touched) && (
									<CerebroText variant="error">{errors.yearsInOperation}</CerebroText>
								)}
							</Grid>
						</Grid>

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item xs={12} md={6}>
								<CerebroLabel required id="numberEmployees">
									How many US employees?
								</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="numberEmployees"
										as={CerebroMaskedInput}
										name="numberEmployees"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('numberEmployees', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
								{FormikUtils.IsInvalid('numberEmployees', errors, touched) && (
									<CerebroText variant="error">{errors.numberEmployees}</CerebroText>
								)}
							</Grid>

							<Grid item xs={12} md={6}>
								<CerebroLabel id="nonUsBasedEmployees">How many non-US employees?</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="nonUsBasedEmployees"
										as={CerebroMaskedInput}
										name="nonUsBasedEmployees"
										disabled={isSubmitting}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						<CerebroLabel htmlFor="ownershipStructure" sx={{ marginTop: 3 }}>
							What is the company ownership structure?
						</CerebroLabel>
						{companyQuery.isLoading ? (
							<Skeleton />
						) : (
							<Field
								id="ownershipStructure"
								as={CerebroDropdown}
								name="ownershipStructure"
								disabled={isSubmitting}
							>
								{Object.entries(OwnershipStructureMap)
									.sort()
									.map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
							</Field>
						)}

						<CerebroLabel id="institutionalInvestor" sx={{ marginTop: 3 }}>
							Is the company backed by an institutional investor?
						</CerebroLabel>
						{dealQuery.isSuccess ? (
							<Field
								id="institutionalInvestor"
								as={CerebroYesNo}
								name="institutionalInvestor"
								disabled={isSubmitting}
							/>
						) : (
							<Skeleton height={60} />
						)}

						<CerebroText sx={{ marginTop: 3 }}>
							What percentage of your business comes from each of the following?{' '}
							<CerebroText component="span" variant="hint">
								(answers should total 100)
							</CerebroText>
						</CerebroText>
						<Grid container spacing={3}>
							<Grid item xs={12} md={4}>
								<CerebroLabel id="b2bPercent">B2B</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="b2bPercent"
										as={CerebroMaskedInput}
										name="b2bPercent"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('b2bPercent', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>

							<Grid item xs={12} md={4}>
								<CerebroLabel id="b2cPercent">B2C</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="b2cPercent"
										as={CerebroMaskedInput}
										name="b2cPercent"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('b2cPercent', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>

							<Grid item xs={12} md={4}>
								<CerebroLabel id="b2gPercent">B2G</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="b2gPercent"
										as={CerebroMaskedInput}
										name="b2gPercent"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('b2gPercent', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						<CerebroLabel htmlFor="customerConcentrationsMoreThan10Percent" sx={{ marginTop: 3 }}>
							Does the company have any customer concentrations that account for more than 10% of revenue?
						</CerebroLabel>
						<Field
							id="customerConcentrationsMoreThan10Percent"
							as={CerebroYesNo}
							name="customerConcentrationsMoreThan10Percent"
							disabled={isSubmitting}
						/>

						{values.customerConcentrationsMoreThan10Percent && (
							<>
								<CerebroLabel htmlFor="customerConcentrationDetails" sx={{ marginTop: 3 }}>
									Please describe
								</CerebroLabel>
								<Field
									id="customerConcentrationDetails"
									as={CerebroTextarea}
									name="customerConcentrationDetails"
									disabled={isSubmitting}
								/>
							</>
						)}

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save &amp; Continue
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>

			<CerebroDrawer open={ruralAreaInfoOpen} onClose={() => setRuralAreaInfoOpen(false)}>
				<Grid container direction="column" alignItems="center">
					<CerebroIcon sx={theme => ({ color: theme.palette.primary.main, marginBottom: 3 })}>
						<FontAwesomeIcon icon={faInfoCircle} size="3x" />
					</CerebroIcon>
					<CerebroText sx={{ maxWidth: { xs: 'unset', sm: 250 } }}>
						A rural development zone is typically defined as a municipality with a population of 50,000 or
						less.
					</CerebroText>
				</Grid>
			</CerebroDrawer>
		</CerebroPageContainer>
	);
};

function validateForm(values: CompanyBorrowerFormValues) {
	const errors: CompanyBorrowerFormErrors = {};

	if (!values.companyIndustry) {
		errors.companyIndustry = "Please indicate the company's industry";
	}

	if (!values.yearsInOperation) {
		errors.yearsInOperation = 'Please indicate years of operation';
	}

	if (!values.numberEmployees) {
		errors.numberEmployees = 'Please indicate the # of US employees';
	}

	return errors;
}

type CompanyBorrowerFormErrors = FormikErrors<CompanyBorrowerFormValues>;
type DealMutationVariables = FormikUtils.MutateOnSubmitVariables<CompanyBorrowerFormValues>;
interface CompanyBorrowerFormValues {
	naicsCode: NaicsCode | null;
	countryType: CountryTypes;
	stateCode: string;
	postalCode: string;
	otherGeographies: string[];
	companyLocatedInRuralArea: boolean | null;
	companyIndustry: Industry | null;
	yearsInOperation: string;
	numberEmployees: string;
	nonUsBasedEmployees: string;
	ownershipStructure: OwnershipStructure;
	institutionalInvestor: boolean;
	b2bPercent: string;
	b2cPercent: string;
	b2gPercent: string;
	customerConcentrationsMoreThan10Percent: boolean;
	customerConcentrationDetails: string;
	showCreditExplorer: boolean;
}
